import * as React from 'react';
import DiMarkdown from './DiMarkdown';

function Project({project} :any) {
  const [expandedInsight, setExpandedInsight] = React.useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [insightToDelete, setInsightToDelete] = React.useState<string | null>(null);
  const [isPendingTasksExpanded, setIsPendingTasksExpanded] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [copyNotification, setCopyNotification] = React.useState<string | null>(null);
  const itemsPerPage = 5;
  const totalPages = Math.ceil((project.insights?.length || 0) / itemsPerPage);

  const toggleInsight = (id: string) => {
    setExpandedInsight(prevId => prevId === id ? null : id);
  };

  const openDeleteModal = (id: string) => {
    setInsightToDelete(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setInsightToDelete(null);
  };

  const deleteInsight = async () => {
    if (insightToDelete) {
      try {
        const response = await fetch(`/insights/${insightToDelete}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          },
        });

        if (response.ok) {
          project.insights = project.insights.filter((insight) => insight.id !== insightToDelete);
          closeDeleteModal();
        } else {
          console.error('Failed to delete insight');
        }
      } catch (error) {
        console.error('Error deleting insight:', error);
      }
    }
  };

  const togglePendingTasks = () => {
    setIsPendingTasksExpanded(!isPendingTasksExpanded);
  };

  const getCurrentPageInsights = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return project.insights?.slice(startIndex, startIndex + itemsPerPage) || [];
  };

  const copyToClipboard = (content: string, insightId: string) => {
    navigator.clipboard.writeText(content);
    setCopyNotification(insightId);
    // Clear the notification after 2 seconds
    setTimeout(() => {
      setCopyNotification(null);
    }, 2000);
  };

  return (
    <div>
      <div className="page-header">
        <h3 className="page-title"> </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item"><a href="/my_projects">My Projects</a></li>
            <li className="breadcrumb-item active" aria-current="page">{project.title}</li>
          </ol>
        </nav>
      </div>
      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">{project.title}</h4>
              <p className="card-description">
                {project.description}
              </p>

              {/* Updated Pending Tasks Section */}
              <h5 className="mt-4 mb-3 pending-tasks-header" onClick={togglePendingTasks}>
                Pending Tasks
                <span className="toggle-icon">{isPendingTasksExpanded ? '▼' : '▶'}</span>
              </h5>
              {isPendingTasksExpanded && (
                <div className="row">
                  <div className="pending-tasks col-lg-9 col-md-12">
                    {project.pending_tasks && project.pending_tasks.length > 0 ? (
                      <ul className="task-list">
                        {project.pending_tasks.map((task, index) => (
                          <li key={index} className="task-item">
                            <div className="task-main">
                              <span className="task-icon">
                                {(task.task_type === 'immediate') || (task.task_type === 'conclusion') ? '⚡' : '🔄'}
                              </span>
                              <div className="task-content">
                                <div className="task-text">{task.content}</div>
                                <div className="task-details">
                                  <div className="task-badges">
                                    <span className="task-badge">
                                      {task.task_type === 'immediate' ? 'one-time' : task.task_type}
                                    </span>
                                    {task.recurring_frequency && task.task_type !== 'immediate' && (
                                      <span className="task-frequency">
                                        {task.recurring_frequency}
                                      </span>
                                    )}
                                  </div>
                                  {task.last_run_at && (
                                    <span className="task-last-run">
                                      Last run: {task.last_run_at}
                                    </span>
                                  )}
                                </div>
                                {task.task_type === 'recurring' && (task.incident_trigger || task.incident_response_plan) && (
                                  <div className="incident-info">
                                    {task.incident_trigger && (
                                      <div className="incident-section">
                                        <span className="incident-label">Trigger:</span>
                                        <span className="incident-text">{task.incident_trigger}</span>
                                      </div>
                                    )}
                                    {task.incident_response_plan && (
                                      <div className="incident-section">
                                        <span className="incident-label">Response Plan:</span>
                                        <span className="incident-text">{task.incident_response_plan}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="no-tasks">No pending tasks for this project.</p>
                    )}
                  </div>
                </div>
              )}

              <h5 className="mt-4 mb-3">Insights</h5>
              <div className="row">
                <div className="col-lg-9 col-md-12">
                  {project.insights && project.insights.length > 0 ? (
                    <>
                      {getCurrentPageInsights().map((insight) => (
                        <div key={insight.id} className="insight-card" onClick={() => toggleInsight(insight.id)}>
                          <div className="insight-header">
                            <h6 className="insight-title">{insight.title}</h6>
                            <div className="insight-meta">
                              <span>AI: {insight.ai_assistant}</span>
                              <span>Created: {insight.created_at}</span>
                            </div>
                          </div>
                          {expandedInsight === insight.id && (
                            <div className="insight-content">
                              <p>
                                <DiMarkdown>{insight.content}</DiMarkdown>
                              </p>
                              <p><strong>Task:</strong> {insight.task}</p>
                            </div>
                          )}
                          <div className="insight-actions">
                            <button onClick={(e) => {
                              e.stopPropagation();
                              toggleInsight(insight.id);
                            }} className="expand-btn">
                              {expandedInsight === insight.id ? 'Collapse' : 'Expand'}
                            </button>
                            <button onClick={(e) => {
                              e.stopPropagation();
                              // Copy insight content to clipboard
                              copyToClipboard(insight.content, insight.id);
                            }} className="expand-btn" title="Copy Insight">
                              Copy
                            </button>
                            <button onClick={(e) => {
                              e.stopPropagation();
                              // Download insight content as a text file
                              const element = document.createElement("a");
                              const file = new Blob([insight.content], { type: 'text/plain' });
                              element.href = URL.createObjectURL(file);
                              element.download = `${insight.title?.slice(0, 20) || 'insight'}_insight.txt`;
                              document.body.appendChild(element);
                              element.click();
                              document.body.removeChild(element);
                            }} className="expand-btn" title="Download Insight">
                              Download
                            </button>
                            <button onClick={(e) => {
                              e.stopPropagation();
                              openDeleteModal(insight.id);
                            }} className="delete-btn">
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}

                      {totalPages > 1 && (
                        <div className="pagination">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="pagination-btn"
                          >
                            Previous
                          </button>
                          <span className="pagination-info">
                            Page {currentPage} of {totalPages}
                          </span>
                          <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="pagination-btn"
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="no-insights">
                      <p>No insights available for this project.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Custom Delete Confirmation Modal */}
              {showDeleteModal && (
                <div className="modal-overlay">
                  <div className="modal-content">
                    <h2>Confirm Deletion</h2>
                    <p>Are you sure you want to delete this insight?</p>
                    <div className="modal-actions">
                      <button onClick={closeDeleteModal} className="cancel-btn">Cancel</button>
                      <button onClick={deleteInsight} className="confirm-delete-btn">Delete</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Copy Notification */}
              {copyNotification && (
                <div className="copy-notification">
                  <span>Copied to clipboard!</span>
                </div>
              )}

              <style>{`
                .insight-card {
                  border: 1px solid #e0e0e0;
                  border-radius: 0.5rem;
                  padding: 1rem;
                  margin-bottom: 1rem;
                  cursor: pointer;
                  transition: background-color 0.3s ease;
                }
                .insight-card:hover {
                  background-color: #f8f9fa;
                }
                .insight-header {
                  display: flex;
                  flex-direction: column;
                  margin-bottom: 0.5rem;
                }
                .insight-title {
                  margin-bottom: 0.5rem;
                }
                .insight-meta {
                  font-size: 0.875rem;
                  color: #6c757d;
                }
                .insight-meta span {
                  margin-left: 1rem;
                }
                .insight-summary {
                  margin-bottom: 0.5rem;
                }
                .insight-content {
                  margin-top: 0.5rem;
                }
                .insight-actions {
                  display: flex;
                  justify-content: flex-end;
                  margin-top: 0.5rem;
                  flex-wrap: wrap;
                  gap: 0.5rem;
                }
                .insight-actions button {
                  margin-left: 0;
                  padding: 0.25rem 0.5rem;
                  border: none;
                  border-radius: 0.25rem;
                  cursor: pointer;
                  white-space: nowrap;
                }
                .delete-btn {
                  background-color: #dc3545;
                  color: white;
                }
                .expand-btn {
                  background-color: #e0e0e0;
                  color: #333333;
                  padding: 0.25rem 0.5rem;
                  border: none;
                  border-radius: 0.25rem;
                  cursor: pointer;
                  font-weight: 500;
                }
                .expand-btn:hover {
                  background-color: #d0d0d0;
                }
                @media (max-width: 768px) {
                  .insight-header {
                    flex-direction: column;
                    align-items: flex-start;
                  }
                  .insight-meta span {
                    display: block;
                    margin-left: 0;
                  }
                  .insight-actions {
                    justify-content: flex-start;
                  }
                  .insight-actions button {
                    flex: 1;
                    min-width: 100px;
                    text-align: center;
                  }
                }
                .no-insights {
                  text-align: center;
                  padding: 2rem;
                  background-color: #f8f9fa;
                  border-radius: 0.5rem;
                  color: #6c757d;
                }
                .row {
                  display: flex;
                  flex-wrap: wrap;
                }
                .col-lg-9 {
                  width: 75%;
                }
                @media (max-width: 992px) {
                  .col-lg-9 {
                    width: 100%;
                  }
                }

                .modal-overlay {
                  position: fixed;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background-color: rgba(0, 0, 0, 0.5);
                  display: flex;
                  justify-content: center;
                  align-items: center;
                }

                .modal-content {
                  background-color: white;
                  padding: 2rem;
                  border-radius: 0.5rem;
                  width: 90%;
                  max-width: 500px;
                }

                .modal-actions {
                  display: flex;
                  justify-content: flex-end;
                  margin-top: 1rem;
                }

                .modal-actions button {
                  margin-left: 0.5rem;
                  padding: 0.5rem 1rem;
                  border: none;
                  border-radius: 0.25rem;
                  cursor: pointer;
                }

                .cancel-btn {
                  background-color: #6c757d;
                  color: white;
                }

                .confirm-delete-btn {
                  background-color: #dc3545;
                  color: white;
                }

                .pending-tasks {
                  margin-bottom: 2rem;
                }
                .task-list {
                  list-style-type: none;
                  padding: 0;
                }
                .task-item {
                  background-color: #ffffff;
                  border: 1px solid #e0e0e0;
                  border-radius: 0.5rem;
                  padding: 1rem;
                  margin-bottom: 0.75rem;
                  transition: box-shadow 0.2s ease;
                }
                .task-item:hover {
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
                .task-main {
                  display: flex;
                  gap: 1rem;
                }
                .task-icon {
                  font-size: 1.5rem;
                  min-width: 2rem;
                  display: flex;
                  align-items: flex-start;
                }
                .task-content {
                  flex-grow: 1;
                  min-width: 0; /* Prevents flex items from overflowing */
                }
                .task-text {
                  font-size: 1rem;
                  color: #2c3e50;
                  margin-bottom: 0.5rem;
                  font-weight: 500;
                  word-wrap: break-word; /* Ensures long text wraps properly */
                }
                .task-details {
                  display: flex;
                  flex-direction: column;
                  gap: 0.5rem;
                  margin-bottom: 0.75rem;
                }
                .task-badges {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 0.5rem;
                  align-items: center;
                }
                .task-badge {
                  background-color: #e9ecef;
                  color: #495057;
                  padding: 0.25rem 0.75rem;
                  border-radius: 1rem;
                  font-size: 0.875rem;
                  text-transform: capitalize;
                }
                .task-last-run {
                  color: #6c757d;
                  font-size: 0.875rem;
                }
                .incident-info {
                  background-color: #f8f9fa;
                  border-radius: 0.375rem;
                  padding: 0.75rem;
                  margin-top: 0.5rem;
                  word-wrap: break-word; /* Ensures long text wraps properly */
                }
                .incident-section {
                  margin-bottom: 0.5rem;
                }
                .incident-section:last-child {
                  margin-bottom: 0;
                }
                .incident-label {
                  color: #495057;
                  font-weight: 500;
                  margin-right: 0.5rem;
                }
                .incident-text {
                  color: #6c757d;
                }
                .pending-tasks-header {
                  cursor: pointer;
                  user-select: none;
                  display: flex;
                  align-items: center;
                }
                .toggle-icon {
                  margin-left: 0.5rem;
                  font-size: 0.8rem;
                }
                .task-frequency {
                  color: #6c757d;
                  font-size: 0.875rem;
                }
                .pagination {
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  margin-top: 2rem;
                  gap: 1rem;
                }

                .pagination-btn {
                  padding: 0.5rem 1rem;
                  border: 1px solid #dee2e6;
                  background-color: white;
                  border-radius: 0.25rem;
                  cursor: pointer;
                }

                .pagination-btn:disabled {
                  background-color: #e9ecef;
                  cursor: not-allowed;
                }

                .pagination-info {
                  color: #6c757d;
                }

                @media (min-width: 768px) {
                  .task-details {
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;
                  }
                }

                .copy-notification {
                  position: fixed;
                  bottom: 20px;
                  right: 20px;
                  background-color: #4caf50;
                  color: white;
                  padding: 10px 20px;
                  border-radius: 4px;
                  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
                  z-index: 1000;
                  animation: fadeInOut 2s ease-in-out;
                }

                @keyframes fadeInOut {
                  0% { opacity: 0; transform: translateY(20px); }
                  15% { opacity: 1; transform: translateY(0); }
                  85% { opacity: 1; transform: translateY(0); }
                  100% { opacity: 0; transform: translateY(-20px); }
                }
              `}</style>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Project;
